import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  User, Heart, FileText, Settings, MapPin, Calendar, Users,
  Trash2, ArrowLeft, Loader2, Check, Clock, DollarSign
} from 'lucide-react';
import Navbar from '@/components/zera/Navbar';
import Footer from '@/components/zera/Footer';
import ZeraPartners from '@/components/zera/ZeraPartners';
import AuthModal from '@/components/zera/AuthModal';

type ProfileTab = 'profile' | 'saved' | 'journeys' | 'preferences';

const statusColors: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-green-50 text-green-700 border-green-200',
  completed: 'bg-blue-50 text-blue-700 border-blue-200',
  cancelled: 'bg-red-50 text-red-600 border-red-200',
};

const Profile: React.FC = () => {
  const {
    user, profile, loading, savedDestinations, journeyRequests,
    updateProfile, unsaveDestination, setShowAuthModal,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as ProfileTab | null;

  const [activeTab, setActiveTab] = useState<ProfileTab>(tabParam || 'profile');
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  // Profile form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [travelStyle, setTravelStyle] = useState('');
  const [preferredBudget, setPreferredBudget] = useState('');
  const [dietaryRequirements, setDietaryRequirements] = useState('');
  const [specialNotes, setSpecialNotes] = useState('');

  useEffect(() => {
    if (tabParam) setActiveTab(tabParam);
  }, [tabParam]);

  useEffect(() => {
    if (!loading && !user) {
      setShowAuthModal(true);
      navigate('/');
    }
  }, [user, loading, navigate, setShowAuthModal]);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      setPhone(profile.phone || '');
      setTravelStyle(profile.travel_style || '');
      setPreferredBudget(profile.preferred_budget || '');
      setDietaryRequirements(profile.dietary_requirements || '');
      setSpecialNotes(profile.special_notes || '');
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    setSaving(true);
    const { error } = await updateProfile({
      first_name: firstName,
      last_name: lastName,
      phone,
    });
    setSaving(false);
    if (!error) {
      setSaveSuccess(true);
      setEditMode(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    const { error } = await updateProfile({
      travel_style: travelStyle,
      preferred_budget: preferredBudget,
      dietary_requirements: dietaryRequirements,
      special_notes: specialNotes,
    });
    setSaving(false);
    if (!error) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const handleRemoveDestination = async (destId: number) => {
    setRemovingId(destId);
    await unsaveDestination(destId);
    setRemovingId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A961] animate-spin" />
      </div>
    );
  }


  if (!user) return <AuthModal />;

  const tabs: { id: ProfileTab; label: string; icon: React.ElementType }[] = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'saved', label: 'Saved', icon: Heart },
    { id: 'journeys', label: 'Journeys', icon: FileText },
    { id: 'preferences', label: 'Preferences', icon: Settings },
  ];

  const inputClass =
    'w-full bg-white border border-[#E8DFD0] px-4 py-3 font-sans text-sm text-[#2C2C2C] placeholder:text-[#bbb] focus:outline-none focus:border-[#C9A961] transition-colors disabled:bg-[#F5F1E8] disabled:text-[#999]';

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <Navbar />
      <AuthModal />

      {/* Header Banner */}
      <div className="pt-28 lg:pt-36 pb-12 bg-[#1A3B5C]">

        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 font-sans text-[10px] tracking-[0.2em] uppercase text-white/50 hover:text-white/80 transition-colors mb-6"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
          </button>
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#C9A961]/20 border-2 border-[#C9A961]/40 flex items-center justify-center">
              <span className="font-serif text-2xl md:text-3xl text-[#C9A961]">
                {(firstName[0] || user.email?.[0] || 'U').toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl text-white font-light">
                {firstName && lastName ? `${firstName} ${lastName}` : 'Welcome'}
              </h1>
              <p className="font-sans text-xs text-white/50 mt-1">{user.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-[#E8DFD0] bg-white sticky top-16 lg:top-[72px] z-30">

        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="flex overflow-x-auto gap-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 font-sans text-[11px] tracking-[0.15em] uppercase border-b-2 transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-[#C9A961] text-[#1A3B5C]'
                    : 'border-transparent text-[#999] hover:text-[#1A3B5C]'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'saved' && savedDestinations.length > 0 && (
                  <span className="bg-[#C9A961] text-white text-[9px] w-5 h-5 rounded-full flex items-center justify-center">
                    {savedDestinations.length}
                  </span>
                )}
                {tab.id === 'journeys' && journeyRequests.length > 0 && (
                  <span className="bg-[#1A3B5C] text-white text-[9px] w-5 h-5 rounded-full flex items-center justify-center">
                    {journeyRequests.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-10 min-h-[60vh]">
        {/* Success Toast */}
        {saveSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 flex items-center gap-3 animate-fade-in">
            <Check className="w-5 h-5 text-green-600" />
            <span className="font-sans text-sm text-green-700">Changes saved successfully</span>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-serif text-2xl text-[#1A3B5C]">Personal Information</h2>
                <p className="font-sans text-xs text-[#999] mt-1">Manage your account details</p>
              </div>
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="font-sans text-[10px] tracking-[0.2em] uppercase px-6 py-2.5 border border-[#1A3B5C] text-[#1A3B5C] hover:bg-[#1A3B5C] hover:text-white transition-all"
                >
                  Edit
                </button>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setEditMode(false);
                      if (profile) {
                        setFirstName(profile.first_name || '');
                        setLastName(profile.last_name || '');
                        setPhone(profile.phone || '');
                      }
                    }}
                    className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 text-[#999] hover:text-[#2C2C2C] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="font-sans text-[10px] tracking-[0.2em] uppercase px-6 py-2.5 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Save
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white p-8 border border-[#E8DFD0] space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={!editMode}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={!editMode}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={user.email || ''}
                  disabled
                  className={`${inputClass} !bg-[#F5F1E8] !text-[#999]`}
                />
                <p className="font-sans text-[10px] text-[#bbb] mt-1">Email cannot be changed</p>
              </div>
              <div>
                <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={!editMode}
                  className={inputClass}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div className="pt-4 border-t border-[#E8DFD0]">
                <p className="font-sans text-[10px] text-[#bbb]">
                  Member since {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}

                </p>
              </div>
            </div>
          </div>
        )}

        {/* Saved Destinations Tab */}
        {activeTab === 'saved' && (
          <div>
            <div className="mb-8">
              <h2 className="font-serif text-2xl text-[#1A3B5C]">Saved Destinations</h2>
              <p className="font-sans text-xs text-[#999] mt-1">
                Your collection of dream destinations
              </p>
            </div>

            {savedDestinations.length === 0 ? (
              <div className="text-center py-20 bg-white border border-[#E8DFD0]">
                <Heart className="w-12 h-12 text-[#E8DFD0] mx-auto mb-4" />
                <h3 className="font-serif text-xl text-[#1A3B5C] mb-2">No Saved Destinations Yet</h3>
                <p className="font-sans text-xs text-[#999] mb-6 max-w-sm mx-auto">
                  Explore our collection and save the destinations that inspire you.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="font-sans text-[10px] tracking-[0.2em] uppercase px-8 py-3 bg-[#1A3B5C] text-white hover:bg-[#2a5580] transition-colors"
                >
                  Explore Destinations
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedDestinations.map((sd) => (
                  <div key={sd.id} className="bg-white border border-[#E8DFD0] group">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={sd.destination_image}
                        alt={sd.destination_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <button
                        onClick={() => handleRemoveDestination(sd.destination_id)}
                        disabled={removingId === sd.destination_id}
                        className="absolute top-3 right-3 w-9 h-9 bg-white/90 flex items-center justify-center hover:bg-red-50 transition-colors"
                        aria-label="Remove"
                      >
                        {removingId === sd.destination_id ? (
                          <Loader2 className="w-4 h-4 text-[#999] animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 text-[#999] hover:text-red-500" />
                        )}
                      </button>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-1.5 mb-2">
                        <MapPin className="w-3 h-3 text-[#C9A961]" />
                        <span className="font-sans text-[10px] tracking-wider text-[#999]">
                          {sd.destination_location}
                        </span>
                      </div>
                      <h3 className="font-serif text-lg text-[#1A3B5C] mb-2">{sd.destination_name}</h3>
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => navigate('/booking')}
                          className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 bg-[#1A3B5C]/10 text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors"
                        >
                          Book Now
                        </button>
                        <button
                          onClick={() => navigate('/')}
                          className="font-sans text-[10px] tracking-wider uppercase text-[#C9A961] hover:text-[#b8944d] transition-colors"
                        >
                          View
                        </button>
                      </div>

                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Journey Requests Tab */}
        {activeTab === 'journeys' && (
          <div>
            <div className="mb-8">
              <h2 className="font-serif text-2xl text-[#1A3B5C]">Journey Requests</h2>
              <p className="font-sans text-xs text-[#999] mt-1">
                Track the status of your travel inquiries
              </p>
            </div>

            {journeyRequests.length === 0 ? (
              <div className="text-center py-20 bg-white border border-[#E8DFD0]">
                <FileText className="w-12 h-12 text-[#E8DFD0] mx-auto mb-4" />
                <h3 className="font-serif text-xl text-[#1A3B5C] mb-2">No Journey Requests</h3>
                <p className="font-sans text-xs text-[#999] mb-6 max-w-sm mx-auto">
                  Start planning your next extraordinary journey with our concierge team.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="font-sans text-[10px] tracking-[0.2em] uppercase px-8 py-3 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-colors"
                >
                  Plan a Journey
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {journeyRequests.map((jr) => (
                  <div key={jr.id} className="bg-white border border-[#E8DFD0] p-6 md:p-8">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                      <div>
                        <h3 className="font-serif text-xl text-[#1A3B5C] mb-1">{jr.destination}</h3>
                        {jr.experience && (
                          <span className="font-sans text-[10px] tracking-wider uppercase text-[#C9A961]">
                            {jr.experience.replace(/-/g, ' ')}
                          </span>
                        )}
                      </div>
                      <span
                        className={`inline-flex items-center px-3 py-1.5 font-sans text-[10px] tracking-wider uppercase border ${
                          statusColors[jr.status] || statusColors.pending
                        }`}
                      >
                        {jr.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-start gap-2">
                        <Calendar className="w-4 h-4 text-[#C9A961] mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-sans text-[10px] uppercase text-[#999] block">Check-in</span>
                          <span className="font-sans text-xs text-[#2C2C2C]">
                            {jr.check_in ? new Date(jr.check_in).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Calendar className="w-4 h-4 text-[#C9A961] mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-sans text-[10px] uppercase text-[#999] block">Check-out</span>
                          <span className="font-sans text-xs text-[#2C2C2C]">
                            {jr.check_out ? new Date(jr.check_out).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-[#C9A961] mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-sans text-[10px] uppercase text-[#999] block">Guests</span>
                          <span className="font-sans text-xs text-[#2C2C2C]">{jr.guests}</span>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <DollarSign className="w-4 h-4 text-[#C9A961] mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-sans text-[10px] uppercase text-[#999] block">Budget</span>
                          <span className="font-sans text-xs text-[#2C2C2C]">{jr.budget || 'Not specified'}</span>
                        </div>
                      </div>
                    </div>

                    {jr.special_requests && (
                      <div className="pt-4 border-t border-[#E8DFD0]">
                        <span className="font-sans text-[10px] uppercase text-[#999] block mb-1">Special Requests</span>
                        <p className="font-sans text-xs text-[#666] leading-relaxed">{jr.special_requests}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#E8DFD0]">
                      <Clock className="w-3 h-3 text-[#bbb]" />
                      <span className="font-sans text-[10px] text-[#bbb]">
                        Submitted {new Date(jr.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="max-w-2xl">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-serif text-2xl text-[#1A3B5C]">Travel Preferences</h2>
                <p className="font-sans text-xs text-[#999] mt-1">
                  Help us personalize your experiences
                </p>
              </div>
              <button
                onClick={handleSavePreferences}
                disabled={saving}
                className="font-sans text-[10px] tracking-[0.2em] uppercase px-6 py-2.5 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Save Preferences
              </button>
            </div>

            <div className="bg-white p-8 border border-[#E8DFD0] space-y-6">
              <div>
                <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                  Travel Style
                </label>
                <select
                  value={travelStyle}
                  onChange={(e) => setTravelStyle(e.target.value)}
                  className={`${inputClass} appearance-none cursor-pointer`}
                >
                  <option value="">Select your travel style</option>
                  <option value="relaxation">Relaxation & Wellness</option>
                  <option value="adventure">Adventure & Exploration</option>
                  <option value="cultural">Cultural & Heritage</option>
                  <option value="romantic">Romantic & Celebration</option>
                  <option value="family">Family & Multi-generational</option>
                  <option value="culinary">Culinary & Gastronomic</option>
                </select>
              </div>

              <div>
                <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                  Preferred Budget Range
                </label>
                <select
                  value={preferredBudget}
                  onChange={(e) => setPreferredBudget(e.target.value)}
                  className={`${inputClass} appearance-none cursor-pointer`}
                >
                  <option value="">Prefer not to say</option>
                  <option value="5000-10000">$5,000 - $10,000</option>
                  <option value="10000-25000">$10,000 - $25,000</option>
                  <option value="25000-50000">$25,000 - $50,000</option>
                  <option value="50000-100000">$50,000 - $100,000</option>
                  <option value="100000+">$100,000+</option>
                </select>
              </div>

              <div>
                <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                  Dietary Requirements
                </label>
                <input
                  type="text"
                  value={dietaryRequirements}
                  onChange={(e) => setDietaryRequirements(e.target.value)}
                  className={inputClass}
                  placeholder="e.g., Vegetarian, Gluten-free, Halal..."
                />
              </div>

              <div>
                <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1.5">
                  Special Notes for Our Concierge
                </label>
                <textarea
                  value={specialNotes}
                  onChange={(e) => setSpecialNotes(e.target.value)}
                  className={`${inputClass} min-h-[120px] resize-none`}
                  placeholder="Accessibility needs, preferred room types, celebration details, or anything else we should know..."
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <ZeraPartners />
      <Footer />
    </div>
  );
};

export default Profile;
